import { logger } from "@/lib/logger";
import { apiRequest } from "@/lib/queryClient";
interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface Restaurant {
  id: string;
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  category?: string;
  priceRange?: string;
}

interface LocationSession {
  id: string;
  userId: string;
  restaurantId?: string;
  latitude: number;
  longitude: number;
  accuracy: number;
  startTime: Date;
  endTime?: Date;
  duration?: number;
  reviewPrompted: boolean;
  reviewCreated: boolean;
}

interface ReviewPromptData {
  restaurant: Restaurant;
  sessionId: string;
  timeSpent: number; // in minutes
}

export class LocationService {
  private watchId: number | null = null;
  private currentSession: LocationSession | null = null;
  private lastKnownLocation: LocationCoordinates | null = null;
  private sessionStartTime: Date | null = null;
  private reviewPromptThreshold = 15; // minutes
  private locationCheckInterval = 30000; // 30 seconds
  private maxDistanceThreshold = 50; // meters
  private monitoringGeneration = 0;
  private providerUnavailable = false;
  private sessionWritesUnavailable = false;
  private sessionStartInFlight = false;

  // Callbacks
  private onReviewPromptCallback: ((data: ReviewPromptData) => void) | null =
    null;
  private onLocationUpdateCallback:
    | ((location: LocationCoordinates) => void)
    | null = null;

  constructor() {
    this.bindMethods();
  }

  private bindMethods() {
    this.handleLocationUpdate = this.handleLocationUpdate.bind(this);
    this.handleLocationError = this.handleLocationError.bind(this);
  }

  /**
   * Start monitoring user location for restaurant detection
   */
  async startLocationMonitoring(userId: string): Promise<boolean> {
    // Browser visitors have no local user row to attach a session to.
    // Home must not request permission for guests.
    if (!userId || userId === "guest-user" || userId === "guest") return false;
    if (!navigator.geolocation) {
      logger.warn("Geolocation not supported by this browser");
      return false;
    }
    const generation = this.monitoringGeneration;

    // Monitoring is background-only: requesting permission here would interrupt
    // first paint (and surprise visitors). An explicit location picker may use
    // getCurrentLocation() instead.
    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });
      if (permission.state !== "granted") {
        return false;
      }
    } catch {
      return false;
    }

    if (generation !== this.monitoringGeneration) return false;
    if (this.watchId !== null) return true;
    ++this.monitoringGeneration;
    const watchGeneration = this.monitoringGeneration;
    // Start watching position with high accuracy
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        void this.handleLocationUpdate(position, watchGeneration);
      },
      this.handleLocationError,
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds
        maximumAge: 30000, // 30 seconds
      }
    );

    logger.log("Location monitoring started");
    return true;
  }

  /**
   * Stop location monitoring
   */
  stopLocationMonitoring(): void {
    ++this.monitoringGeneration;
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    // End current session if active
    if (this.currentSession) {
      void this.endCurrentSession();
    }
    this.lastKnownLocation = null;
    this.providerUnavailable = false;
    this.sessionWritesUnavailable = false;

    logger.log("Location monitoring stopped");
  }

  /**
   * Set callback for when review prompt should be triggered
   */
  onReviewPrompt(callback: (data: ReviewPromptData) => void): void {
    this.onReviewPromptCallback = callback;
  }

  /**
   * Set callback for location updates
   */
  onLocationUpdate(callback: (location: LocationCoordinates) => void): void {
    this.onLocationUpdateCallback = callback;
  }

  /**
   * Handle location updates from GPS
   */
  private async handleLocationUpdate(
    position: GeolocationPosition,
    generation: number
  ): Promise<void> {
    const coords: LocationCoordinates = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };

    if (generation !== this.monitoringGeneration ||
        !Number.isFinite(coords.latitude) || Math.abs(coords.latitude) > 90 ||
        !Number.isFinite(coords.longitude) || Math.abs(coords.longitude) > 180 ||
        !Number.isFinite(coords.accuracy) || coords.accuracy < 0) return;

    // Call location update callback
    if (this.onLocationUpdateCallback) {
      this.onLocationUpdateCallback(coords);
    }

    // Record the fix before network I/O so overlapping watch callbacks do not
    // start duplicate sessions against the same position.
    const moved = this.hasMovedSignificantly(coords);
    this.lastKnownLocation = coords;
    if (this.sessionStartInFlight) return;

    // Check if user has moved significantly
    if (moved) {
      // End current session if user moved away
      if (this.currentSession) {
        await this.endCurrentSession();
      }

      // Start new session at new location
      await this.startNewSession(coords, generation);
    } else if (this.currentSession) {
      // Update current session duration
      await this.updateSessionDuration();

      // Check if we should prompt for review
      await this.checkForReviewPrompt();
    }

  }

  /**
   * Handle geolocation errors
   */
  private handleLocationError(error: GeolocationPositionError): void {
    let message = "Unknown location error";

    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = "Location access denied by user";
        break;
      case error.POSITION_UNAVAILABLE:
        message = "Location information unavailable";
        break;
      case error.TIMEOUT:
        message = "Location request timed out";
        break;
    }

    logger.warn("Location error:", message);
    if (error.code === error.PERMISSION_DENIED) this.stopLocationMonitoring();
  }

  /**
   * Check if user has moved significantly from last known location
   */
  private hasMovedSignificantly(newLocation: LocationCoordinates): boolean {
    if (!this.lastKnownLocation) {
      return true; // First location update
    }

    const distance = this.calculateDistance(
      this.lastKnownLocation.latitude,
      this.lastKnownLocation.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    return distance > this.maxDistanceThreshold;
  }

  /**
   * Start a new location session
   */
  private async startNewSession(
    location: LocationCoordinates,
    generation: number
  ): Promise<void> {
    if (this.sessionWritesUnavailable || this.sessionStartInFlight) return;
    this.sessionStartInFlight = true;
    try {
      // Find restaurant at this location
      const restaurant = await this.findRestaurantAtLocation(location);
      if (generation !== this.monitoringGeneration) return;

      // Create new session
      const sessionData = {
        restaurantId: restaurant?.id,
        latitude: location.latitude.toString(),
        longitude: location.longitude.toString(),
        accuracy: location.accuracy.toString(),
        startTime: new Date().toISOString(),
        reviewPrompted: false,
        reviewCreated: false,
      };

      const response = await apiRequest("POST", "/api/location-sessions", sessionData);
      const session: LocationSession = await response.json();
      if (generation !== this.monitoringGeneration) {
        // Navigation/unmount occurred while the request was in flight.
        void apiRequest("PATCH", `/api/location-sessions/${session.id}`, {
          endTime: new Date().toISOString(),
          duration: 0,
        }).catch(() => {});
        return;
      }
      this.currentSession = session;
      this.sessionStartTime = new Date();
    } catch {
      this.sessionWritesUnavailable = true;
      logger.warn("Could not start location session");
    } finally {
      this.sessionStartInFlight = false;
    }
  }

  /**
   * End current location session
   */
  private async endCurrentSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      const endTime = new Date();
      const duration = Math.floor(
        (endTime.getTime() -
          new Date(this.currentSession.startTime).getTime()) /
          60000
      ); // minutes

      await apiRequest("PATCH", `/api/location-sessions/${this.currentSession.id}`, {
        endTime: endTime.toISOString(),
        duration: Math.max(0, duration),
      });
    } catch {
      logger.warn("Could not end location session");
    }

    this.currentSession = null;
    this.sessionStartTime = null;
  }

  /**
   * Update current session duration
   */
  private async updateSessionDuration(): Promise<void> {
    if (!this.currentSession || !this.sessionStartTime) return;

    const duration = Math.floor(
      (Date.now() - this.sessionStartTime.getTime()) / 60000
    ); // minutes

    try {
      await apiRequest("PATCH", `/api/location-sessions/${this.currentSession.id}`, { duration });
    } catch {
      logger.warn("Could not update location session");
    }
  }

  /**
   * Check if user should be prompted for review
   */
  private async checkForReviewPrompt(): Promise<void> {
    if (!this.currentSession || !this.sessionStartTime) return;
    if (this.currentSession.reviewPrompted) return; // Already prompted

    const timeSpent = Math.floor(
      (Date.now() - this.sessionStartTime.getTime()) / 60000
    ); // minutes

    if (
      timeSpent >= this.reviewPromptThreshold &&
      this.currentSession.restaurantId
    ) {
      // Get restaurant details
      const restaurant = await this.getRestaurantById(
        this.currentSession.restaurantId
      );

      if (restaurant && this.onReviewPromptCallback) {
        // Mark as prompted
        this.currentSession.reviewPrompted = true;

        try {
          await apiRequest("PATCH", `/api/location-sessions/${this.currentSession.id}`, {
            reviewPrompted: true,
          });
        } catch {
          logger.warn("Could not record location review prompt");
          return;
        }

        // Trigger review prompt
        this.onReviewPromptCallback({
          restaurant,
          sessionId: this.currentSession.id,
          timeSpent,
        });
      }
    }
  }

  /**
   * Find restaurant at given location
   */
  private async findRestaurantAtLocation(
    location: LocationCoordinates
  ): Promise<Restaurant | null> {
    try {
      const searchRadius = 100; // meters

      const response = await fetch(
        `/api/restaurants/nearby?latitude=${location.latitude}&longitude=${location.longitude}&radius=${searchRadius}`
      );

      if (response.ok) {
        const restaurants: Restaurant[] = await response.json();

        // Return the closest restaurant within threshold
        if (restaurants.length > 0) {
          return restaurants[0]; // API should return closest first
        }
      }

      // Try Google Places API for new restaurants
      return await this.searchGooglePlaces(location);
    } catch {
      logger.warn("Could not find nearby restaurant");
      return null;
    }
  }

  /**
   * Search Google Places API for restaurants
   */
  private async searchGooglePlaces(
    location: LocationCoordinates
  ): Promise<Restaurant | null> {
    if (this.providerUnavailable) return null;
    try {
      const response = await fetch("/api/restaurants/google-places", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          radius: 50, // meters
        }),
      });

      if (response.ok) {
        const restaurant: Restaurant = await response.json();
        return restaurant;
      }
      if (response.status === 503) this.providerUnavailable = true;
      logger.warn("Restaurant lookup unavailable", response.status);
    } catch {
      logger.warn("Restaurant lookup unavailable");
    }

    return null;
  }

  /**
   * Get restaurant details by ID
   */
  private async getRestaurantById(
    restaurantId: string
  ): Promise<Restaurant | null> {
    try {
      const response = await fetch(`/api/restaurants/${restaurantId}`);

      if (response.ok) {
        return await response.json();
      }
    } catch {
      logger.warn("Could not get restaurant details");
    }

    return null;
  }

  /**
   * Calculate distance between two points in meters
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  }

  /**
   * Get current location once (not monitoring)
   */
  async getCurrentLocation(): Promise<LocationCoordinates | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        () => {
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        }
      );
    });
  }

  /**
   * Mark review as created for current session
   */
  async markReviewCreated(): Promise<void> {
    if (!this.currentSession) return;

    try {
      await apiRequest("PATCH", `/api/location-sessions/${this.currentSession.id}`, {
        reviewCreated: true,
      });

      this.currentSession.reviewCreated = true;
    } catch {
      logger.warn("Could not mark location review created");
    }
  }
}

// Export singleton instance
export const locationService = new LocationService();
